import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Input } from "./ui/input";
import { Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StatoAutocomplete({ 
  value, 
  onChange, 
  onSelect,
  placeholder = "Cerca stato...",
  disabled = false,
  className = ""
}) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const isFocusedRef = useRef(false);
  const justSelectedRef = useRef(false);
  const skipNextSearchRef = useRef(false);
  const prevValueRef = useRef(value);

  // Allinea la query solo quando il value *esterno* cambia (evita reset mentre l'utente digita con value fisso)
  useEffect(() => {
    if (justSelectedRef.current) return;
    if (prevValueRef.current !== value) {
      prevValueRef.current = value;
      setQuery(value || "");
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setResults([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search stati when query changes
  useEffect(() => {
    // Skip search if we just selected
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }

    if (justSelectedRef.current) {
      return;
    }

    if (!isFocused) {
      setIsOpen(false);
      return;
    }

    const searchStati = async () => {
      if (query.length < 2) {
        setResults([]);
        setIsOpen(false);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/stati/search`, {
          params: { q: query, limit: 15 }
        });
        
        // Don't open if we just selected or the input lost focus while searching.
        if (!justSelectedRef.current && isFocusedRef.current) {
          setResults(response.data.results || []);
          setIsOpen(true);
          setSelectedIndex(-1);
        }
      } catch (error) {
        console.error("Error searching stati:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchStati, 300);
    return () => clearTimeout(debounce);
  }, [query, isFocused]);

  const handleInputChange = (e) => {
    justSelectedRef.current = false;
    const newValue = e.target.value;
    setQuery(newValue);
    onChange?.(newValue);
  };

  const handleSelect = useCallback((stato) => {
    // Set flags to prevent reopening
    justSelectedRef.current = true;
    skipNextSearchRef.current = true;
    
    // Close dropdown immediately
    setIsOpen(false);
    setResults([]);
    
    // Update query
    setQuery(stato.descrizione);
    
    // Blur input
    if (inputRef.current) {
      inputRef.current.blur();
    }
    
    // Notify parent after state updates
    setTimeout(() => {
      onChange?.(stato.descrizione);
      onSelect?.(stato);
      
      // Reset flag after a delay
      setTimeout(() => {
        justSelectedRef.current = false;
      }, 500);
    }, 10);
  }, [onChange, onSelect]);

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setResults([]);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
    setIsFocused(true);

    // Don't reopen if we just selected
    if (justSelectedRef.current) {
      return;
    }
    if (query.length >= 2 && results.length > 0) {
      setIsOpen(true);
    }
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    setIsFocused(false);
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`h-11 rounded-lg pr-8 ${className}`}
          autoComplete="off"
        />
        {loading && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          </div>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {results.map((stato, index) => (
            <div
              key={`${stato.codice}-${index}`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(stato);
              }}
              className={`px-3 py-2 cursor-pointer text-sm transition-colors ${
                index === selectedIndex 
                  ? "bg-[#3B5998] text-white" 
                  : "hover:bg-slate-100"
              }`}
            >
              <div className="font-medium">{stato.descrizione}</div>
              <div className={`text-xs ${index === selectedIndex ? "text-blue-200" : "text-slate-500"}`}>
                Cod: {stato.codice}
              </div>
            </div>
          ))}
        </div>
      )}

      {isOpen && query.length >= 2 && results.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-center text-sm text-slate-500">
          Nessuno stato trovato
        </div>
      )}
    </div>
  );
}
